// Runs and droughts of a live match, from the play-by-play logs: for the cards next to the live score.
// A run: from a basket of one team up to now, it scored at least RUN_MIN_POINTS and the other team at most a
// quarter of that (never more than RUN_MAX_AGAINST): 8-0, 10-2, 12-3, 16-4. A drought: a team without a point
// for a while of game time

export const RUN_MIN_POINTS = 8
// the card comes again each time the run grows by this much: 8, 12, 16...
export const RUN_STEP_POINTS = 4
const RUN_MAX_AGAINST = 4
// game seconds without a point after which the drought card comes
export const DROUGHT_MARKS = [180, 300]

// 4 periods of 10 minutes, then overtimes of 5, as the stats app counts them
function periodStart(period) {
    return period <= 4 ? (period - 1) * 600 : 2400 + (period - 5) * 300
}

function periodLength(period) {
    return period <= 4 ? 600 : 300
}

export function periodOf(seconds) {
    return seconds < 2400 ? Math.floor(seconds / 600) + 1 : Math.floor((seconds - 2400) / 300) + 5
}

// "mm:ss" from tip-off, in seconds; null when the log has none
function logSeconds(log) {
    const match = /^(\d+):(\d+)$/.exec(String((log && log.timestamp) || '').trim())
    return match ? Number(match[1]) * 60 + Number(match[2]) : null
}

// The game time (seconds from tip-off) of a clock of the stats app: period, and what is left of it (ms)
export function clockSeconds(period, remainingMs) {
    const p = Math.max(1, Number(period) || 1)
    return periodStart(p) + periodLength(p) - Math.max(0, Number(remainingMs) || 0) / 1000
}

// Every basket of the two teams in the order it was made: { team: 1 | 2, points, at (game seconds), logId }
export function getBaskets(logs, team1Id, team2Id) {
    return (Array.isArray(logs) ? logs : [])
        .map((log, index) => {
            const points = (Number(log.twoPointsMade) || 0) * 2 + (Number(log.threePointsMade) || 0) * 3 + (Number(log.freeThrowsMade) || 0)
            const team = Number(log.teamId) === Number(team1Id) ? 1 : Number(log.teamId) === Number(team2Id) ? 2 : 0
            const at = logSeconds(log)
            return { team, points, at, logId: log.logId, createdAt: Number(log.createdAt) || 0, index }
        })
        .filter(basket => basket.team && basket.points > 0 && basket.at !== null)
        .sort((a, b) => a.at - b.at || a.createdAt - b.createdAt || a.index - b.index)
        .map(({ team, points, at, logId }) => ({ team, points, at, logId }))
}

// The run going on now: { team, points, against, startAt (game seconds of its first basket) }, or null.
// Of the stretches that count as a run, the one with the biggest margin; of equal ones the shorter, which gave
// up fewer points (8-0 rather than 10-2)
export function getCurrentRun(baskets) {
    const scored = { 1: 0, 2: 0 }
    let best = null
    for (let i = baskets.length - 1; i >= 0; i--) {
        const basket = baskets[i]
        scored[basket.team] += basket.points
        const points = scored[basket.team]
        const against = scored[3 - basket.team]
        if (points < RUN_MIN_POINTS || against > Math.min(RUN_MAX_AGAINST, Math.floor(points / 4))) continue
        if (!best || points - against > best.points - best.against) {
            best = { team: basket.team, points, against, startAt: basket.at }
        }
    }
    return best
}

// The last step of the run reached (8, 12, 16...): the card comes once per step
export function runStep(run) {
    if (!run) return 0
    return RUN_MIN_POINTS + Math.floor((run.points - RUN_MIN_POINTS) / RUN_STEP_POINTS) * RUN_STEP_POINTS
}

// When each team last scored (game seconds; 0 when it has not scored yet, the drought then runs from tip-off)
export function getLastScored(baskets) {
    const last = { 1: null, 2: null }
    baskets.forEach(basket => {
        last[basket.team] = basket.at
    })
    return last
}

// The last drought mark (seconds) a stretch of `seconds` without a point has reached, 0 for none
export function droughtMark(seconds) {
    return DROUGHT_MARKS.filter(mark => seconds >= mark).pop() || 0
}

export function formatSeconds(seconds) {
    const value = Math.max(0, Math.floor(seconds))
    return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`
}

// "2η περίοδο", "παράταση", "2η παράταση"
export function periodLabel(period) {
    if (period <= 4) return `${period}η περίοδο`
    return period === 5 ? 'παράταση' : `${period - 4}η παράταση`
}
